import React, { useState } from 'react';
import { createMultipleJobs } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { CheckSquare, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';

const WORK_TYPES = [
  { id: 'augoshu_koku_dastoshana', label: 'Augošu koku dastošana' },
  { id: 'caurmera_merishana', label: 'Caurmēra mērīšana' },
  { id: 'cirsmas_stigoshana', label: 'Cirsmas stigošana' },
  { id: 'ipashuma_robezu_atjaunoshana', label: 'Īpašuma robežu atjaunošana' },
  { id: 'robezzimju_atjaunoshana', label: 'Robežzīmju atjaunošana' },
  { id: 'paliekoso_koku_izzimeshana', label: 'Paliekošo koku izzīmēšana izlases cirtē' },
  { id: 'cirsmu_iezimeshana', label: 'Cirsmu iezīmēšana' },
  { id: 'dokumentacijas_sagatavoshana', label: 'Dokumentācijas sagatavošana' },
  { id: 'es_projektu_sagatavoshana', label: 'ES projektu sagatavošana' },
  { id: 'cits', label: 'Cits' },
];

const BulkJobCreation = ({ isOpen, onClose, projectId, clientId, onSuccess }) => {
  const { t } = useLanguage();
  const workTypes = t('portalUi.admin.bulkJob.workTypes', WORK_TYPES);
  const [selectedWorkTypes, setSelectedWorkTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleWorkType = (workTypeId) => {
    setSelectedWorkTypes(prev =>
      prev.includes(workTypeId)
        ? prev.filter(id => id !== workTypeId)
        : [...prev, workTypeId]
    );
  };

  const selectAll = () => {
    setSelectedWorkTypes(workTypes.map(wt => wt.id));
  };

  const deselectAll = () => {
    setSelectedWorkTypes([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedWorkTypes.length === 0) {
      toast.error(t('portalUi.admin.bulkJob.selectError', 'Lūdzu izvēlieties vismaz vienu darba veidu'));
      return;
    }

    setLoading(true);
    try {
      const createdJobs = await createMultipleJobs(
        projectId,
        clientId,
        selectedWorkTypes
      );
      
      toast.success(t('portalUi.admin.bulkJob.successTitle', 'Veiksmīgi izveidoti darbi!'), {
        description: createdJobs.map(j => `• ${j.title}`).join('\n')
      });
      
      setSelectedWorkTypes([]);
      if (onSuccess) {
        onSuccess(createdJobs);
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portalUi.admin.bulkJob.error', 'Neizdevās izveidot darbus'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="bulk-job-title">
            <CheckSquare className="w-5 h-5" />
            {t('portalUi.admin.bulkJob.title', 'Pievienot vairākus darbus projektam')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Work Types Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{t('portalUi.admin.bulkJob.selectLabel', 'Izvēlieties darba veidus:')} *</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={selectAll}
                  data-testid="bulk-job-select-all"
                >
                  {t('portalUi.admin.bulkJob.selectAll', 'Izvēlēties visus')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={deselectAll}
                  data-testid="bulk-job-clear"
                >
                  {t('portalUi.admin.bulkJob.clearAll', 'Notīrīt')}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 border rounded-lg bg-gray-50 max-h-96 overflow-y-auto">
              {workTypes.map(workType => (
                <div
                  key={workType.id}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white transition-colors"
                >
                  <Checkbox
                    id={workType.id}
                    checked={selectedWorkTypes.includes(workType.id)}
                    onCheckedChange={() => toggleWorkType(workType.id)}
                    data-testid={`bulk-job-checkbox-${workType.id}`}
                  />
                  <label
                    htmlFor={workType.id}
                    className="text-sm font-medium leading-none cursor-pointer flex-1"
                  >
                    {workType.label}
                  </label>
                </div>
              ))}
            </div>

            {selectedWorkTypes.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900 font-medium">
                  {t('portalUi.admin.bulkJob.selectedCount', 'Izvēlēti')} <strong>{selectedWorkTypes.length}</strong> {t('portalUi.admin.bulkJob.selectedSuffix', 'darba veidi')}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {t('portalUi.admin.bulkJob.statusNote', 'Visi darbi tiks izveidoti ar statusu "Plānots"')}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              data-testid="bulk-job-cancel"
            >
              {t('portalUi.admin.bulkJob.cancel', 'Atcelt')}
            </Button>
            <Button
              type="submit"
              disabled={loading || selectedWorkTypes.length === 0}
              className="bg-[#1F3D2B] hover:bg-[#2a5039]"
              data-testid="bulk-job-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('portalUi.admin.bulkJob.creating', 'Izveido...')}
                </>
              ) : (
                t('portalUi.admin.bulkJob.createCount', `Izveidot ${selectedWorkTypes.length} darbus`).replace('{count}', selectedWorkTypes.length)
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BulkJobCreation;
